/**
 * The right side of the homepage hero from 1024px (direction B, the owner's pick of 2026-10-24): a turning DNA double
 * helix on a soft tinted panel, with two floating cards that carry real numbers.
 *
 *  - The helix moves (HelixCanvas.tsx, the owner's request of 2026-10-24). Until it starts, and under reduced motion
 *    while its one still frame is drawn, the column shows a fixed SVG of the same helix: the shell paints it before
 *    any script runs, and the canvas draws its first frame on top of it, at the same place and scale, then the SVG
 *    fades out. The SVG's paths were computed once from the mockup's drawHelix (a 320 x 560 box, 2.1 turns,
 *    amplitude 92, 26 steps): one cubic curve per quarter turn for each strand (within 1px of the sine), the dots as
 *    zero-length round-capped strokes, and the rungs grouped into three of the mockup's opacities. Colours come from
 *    .home-helix in index.css, for both themes.
 *  - "Lowest listed": of the rows the prices section shows (useHomePriceRows), the one with the lowest price per mg,
 *    with its compound and vendor count. The label says "listed", not "today": prices are approved by an editor after
 *    a scraper flags a change, so the homepage makes no claim about when they were last checked.
 *  - "Human trials": the most viewed compound whose research stage names a trial phase (trialPhase, from shortStage).
 *    If none does, the card shows how many compound profiles the site has, the figure "How this site works" shows.
 *  - The column is hidden below 1024px, so a phone's first screen stays light. Its box has a fixed height, and both
 *    cards have a fixed size and a skeleton while the data loads, so nothing shifts.
 *
 * KEEP IN SYNC with the static shell in index.html: it paints this column (the helix, the panel and the two cards in
 * their loading state, HeroArtLoading) before any JavaScript runs. src/components/home/heroShell.test.tsx checks it.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { priceTagParts } from "@/components/ds/priceTagFormat";
import { cn } from "@/lib/utils";
import { useHeroFacts } from "@/components/home/homeData";
import { DotSep, Skeleton } from "@/components/home/parts";
import HelixCanvas from "@/components/home/HelixCanvas";

const STRAND_A =
  "M160 20C208.2 40.6 252 61.3 252 81.9C252 102.5 208.2 123.2 160 143.8C111.8 164.4 68 185.1 68 205.7C68 226.3 111.8 247 160 267.6C208.2 288.3 252 308.9 252 329.5C252 350.2 208.2 370.8 160 391.4C111.8 412.1 68 432.7 68 453.3C68 474 111.8 494.6 160 515.2C179.3 523.5 198.5 531.7 214.1 540";
const STRAND_B =
  "M160 20C111.8 40.6 68 61.3 68 81.9C68 102.5 111.8 123.2 160 143.8C208.2 164.4 252 185.1 252 205.7C252 226.3 208.2 247 160 267.6C111.8 288.3 68 308.9 68 329.5C68 350.2 111.8 370.8 160 391.4C208.2 412.1 252 432.7 252 453.3C252 474 208.2 494.6 160 515.2C140.7 523.5 121.5 531.7 105.9 540";
const DOTS_A =
  "M160 20h0M204.7 40h0M238.2 60h0M251.9 80h0M242.5 100h0M212.3 120h0M168.9 140h0M123.3 160h0M86.9 180h0M69 200h0M74 220h0M100.7 240h0M142.3 260h0M188.4 280h0M227.4 300h0M249.3 320h0M248.8 340h0M225.8 360h0M186.3 380h0M140.1 400h0M99 420h0M73.2 440h0M69.3 460h0M88.3 480h0M125.3 500h0M171.1 520h0M214.1 540h0";
const DOTS_B =
  "M160 20h0M115.3 40h0M81.8 60h0M68.1 80h0M77.5 100h0M107.7 120h0M151.1 140h0M196.7 160h0M233.1 180h0M251 200h0M246 220h0M219.3 240h0M177.7 260h0M131.6 280h0M92.6 300h0M70.7 320h0M71.2 340h0M94.2 360h0M133.7 380h0M179.9 400h0M221 420h0M246.8 440h0M250.7 460h0M231.7 480h0M194.7 500h0M148.9 520h0M105.9 540h0";
const RUNGS: [opacity: string, d: string][] = [
  ["0.4", "M242.5 100H77.5M212.3 120H107.7M168.9 140H151.1M123.3 160H196.7M86.9 180H233.1M225.8 360H94.2M186.3 380H133.7M140.1 400H179.9M99 420H221"],
  ["0.6", "M238.2 60H81.8M251.9 80H68.1M69 200H251M74 220H246M249.3 320H70.7M248.8 340H71.2M73.2 440H246.8M69.3 460H250.7"],
  ["0.8", "M204.7 40H115.3M100.7 240H219.3M142.3 260H177.7M188.4 280H131.6M227.4 300H92.6M88.3 480H231.7M125.3 500H194.7M171.1 520H148.9M214.1 540H105.9"],
];

/** The helix drawing alone: decorative, hidden from screen readers. It fades out once the moving helix is drawn. */
export function HelixSvg({ faded = false }: { faded?: boolean }) {
  return (
    <svg
      viewBox="0 0 320 560"
      fill="none"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
      className={cn("home-helix absolute left-1/2 top-0 h-full w-auto -translate-x-1/2 transition-opacity duration-500", faded && "opacity-0")}
    >
      <g className="helix-rung" strokeWidth="2.5">
        {RUNGS.map(([opacity, d]) => (
          <path key={opacity} opacity={opacity} d={d} />
        ))}
      </g>
      <g className="helix-a">
        <path strokeWidth="5" d={STRAND_A} />
        <path strokeWidth="13" d={DOTS_A} />
      </g>
      <g className="helix-b">
        <path strokeWidth="5" d={STRAND_B} />
        <path strokeWidth="13" d={DOTS_B} />
      </g>
    </svg>
  );
}

// Both cards: a fixed size, so the numbers arriving change the text and never the box. One sits low on the left and
// one high on the right, over the panel's edges, as in the mockup. From 1280px they reach a little past the column
// (into the gap on the left and the page gutter on the right), where there is room, so they cover less of the helix.
const CARD = "home-card home-card-blue absolute flex h-[7rem] w-44 flex-col justify-center px-4";
const CARD_LOW = "bottom-10 left-0 xl:-left-6";
const CARD_HIGH = "right-0 top-10 xl:-right-3";
const LABEL = "type-label font-semibold text-muted-foreground";
const VALUE = "mt-1.5 truncate text-3xl font-black leading-none tracking-tight tabular-nums";
const NOTE = "type-label mt-2 truncate text-muted-foreground";

/** The loading state of a card. index.html paints the same markup. */
function CardSkeleton({ place, label }: { place: string; label: string }) {
  return (
    <div className={cn(CARD, place)}>
      <span className={LABEL}>{label}</span>
      <Skeleton className="mt-1.5 h-[1.875rem] w-24" />
      <Skeleton className="mt-2 h-[1.0625rem] w-32" />
    </div>
  );
}

type Facts = ReturnType<typeof useHeroFacts>;

function LowestCard({ lowest, pricesLoading }: Pick<Facts, "lowest" | "pricesLoading">) {
  if (!lowest) {
    // Still loading, or no listing with an mg count among the rows: the skeleton keeps the place either way.
    return pricesLoading ? <CardSkeleton place={CARD_LOW} label="Lowest listed" /> : null;
  }
  const { compound, lowest: listing, vendorCount } = lowest;
  // "$2.33/mg" as a large "$2.33" and a small "/mg" ("/g" under a cent per mg, see formatPricePerMg).
  const headline = priceTagParts({ price: listing.price, totalMg: listing.total_mg }).headline;
  const cut = headline.lastIndexOf("/");
  // Tables keep three decimals under a dollar so their columns line up; this single big figure reads better as
  // "$0.50" than "$0.500", so a trailing third-decimal zero is dropped here only ("$0.025" stays).
  const amount = (cut > 0 ? headline.slice(0, cut) : headline).replace(/^(\$0\.\d\d)0$/, "$1");
  const unit = cut > 0 ? headline.slice(cut) : "";
  return (
    <Link to={`/compound/${compound.slug}`} className={cn(CARD, CARD_LOW, "home-card-link")}>
      <span className={LABEL}>Lowest listed</span>
      <span className={cn(VALUE, "text-foreground")}>
        {amount}
        {unit && <span className="ml-0.5 text-sm font-semibold tracking-normal text-muted-foreground">{unit}</span>}
      </span>
      <span className={NOTE}>
        {compound.name}
        <DotSep />
        {vendorCount} {vendorCount === 1 ? "vendor" : "vendors"}
      </span>
    </Link>
  );
}

function TrialsCard({ trials, compoundCount, compoundsLoading }: Pick<Facts, "trials" | "compoundCount" | "compoundsLoading">) {
  if (compoundsLoading) return <CardSkeleton place={CARD_HIGH} label="Human trials" />;
  if (trials) {
    return (
      <Link to={`/compound/${trials.compound.slug}`} className={cn(CARD, CARD_HIGH, "home-card-link")}>
        <span className={LABEL}>Human trials</span>
        <span className={cn(VALUE, "tone-violet tone-ink")}>{trials.phase}</span>
        <span className={NOTE}>{trials.compound.name}</span>
      </Link>
    );
  }
  // No compound names a trial phase: a different true figure from the same data.
  if (compoundCount === 0) return null;
  return (
    <Link to="/compare" className={cn(CARD, CARD_HIGH, "home-card-link")}>
      <span className={LABEL}>Compound profiles</span>
      <span className={cn(VALUE, "text-foreground")}>{compoundCount.toLocaleString("en-US")}</span>
      <span className={NOTE}>Compare all prices</span>
    </Link>
  );
}

/** The helix column: a fixed-height box, hidden below 1024px, with the panel, the helix and the two cards. */
function HeroArtFrame({ low, high, live = false }: { low: ReactNode; high: ReactNode; live?: boolean }) {
  const [moving, setMoving] = useState(false);
  return (
    <div data-hero="art" className="relative hidden h-[28rem] lg:block xl:h-[31rem]">
      <div aria-hidden="true" className="home-helix-panel absolute inset-x-6 inset-y-8 rounded-[2.5rem]"></div>
      <HelixSvg faded={moving} />
      {live && <HelixCanvas orientation="vertical" onReady={() => setMoving(true)} />}
      {low}
      {high}
    </div>
  );
}

/** The column before its data arrives. index.html paints exactly this (heroShell.test.tsx compares them). */
export function HeroArtLoading() {
  return <HeroArtFrame low={<CardSkeleton place={CARD_LOW} label="Lowest listed" />} high={<CardSkeleton place={CARD_HIGH} label="Human trials" />} />;
}

export default function HeroArt() {
  const facts = useHeroFacts();
  return (
    <HeroArtFrame
      live
      low={<LowestCard lowest={facts.lowest} pricesLoading={facts.pricesLoading} />}
      high={<TrialsCard trials={facts.trials} compoundCount={facts.compoundCount} compoundsLoading={facts.compoundsLoading} />}
    />
  );
}
